import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Icon,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import useCreateRule from '../../../hooks/use-createrule';
import useDeleteRule from '../../../hooks/use-deleterule';
import useGetSampleUsers from '../../../hooks/use-getsampleusers';
import useUpdateRule from '../../../hooks/use-updaterule';
import {
  type Condition,
  type Rule,
  type SampleUser,
} from '../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../utils';
import { OPERATORS_WITH_NO_VALIDATION } from '../../../utils/constants';
import RuleCondition from '../../RuleCondition/RuleCondition';

import invertedMessages from './messages';

import './TabContent.css';

interface TabContentProps {
  rule: Rule;
  isNewRule: boolean;
  refetchRules: () => void;
  resetActiveTab: () => void;
  setIsNewRuleAdded: (key: boolean) => void;
  setNewRuleName: (key: string) => void;
  setErrorMessage: (key: string) => void;
  setShowRemoveSuccessBanner: (key: boolean) => void;
  setShowCriticalBanner: (key: boolean) => void;
}

function TabContent({
  rule,
  isNewRule,
  refetchRules,
  resetActiveTab,
  setIsNewRuleAdded, // setActiveTabToNew,
  setNewRuleName,
  setShowRemoveSuccessBanner,
  setErrorMessage,
  setShowCriticalBanner,
}: TabContentProps) {
  const [updatedCondition, setUpdatedCondition] = useState<Condition>(
    rule.conditions[0]
  );
  const [updatedRule, setUpdatedRule] = useState(rule);
  const [isRuleConfirmed, setIsRuleConfirmed] = useState(true);
  const [isRemoveModal, setIsRemoveModal] = useState(false);
  const [showSampleUsers, setShowSampleUsers] = useState(false);
  const [sampleUsers, setSampleUsers] = useState<SampleUser[]>();

  const isRecommendedRule = rule.status === 2;

  const l = useInvertedLocalize(invertedMessages);

  const {
    mutate: getSampleUsers,
    isSuccess: isGetSampleUsersSuccess,
    isPending: isGetSampleUsersPending,
  } = useGetSampleUsers();
  const {
    mutate: updateRule,
    isSuccess: isUpdateRuleSuccess,
    isPending: isRuleUpdating,
  } = useUpdateRule();
  const {
    mutate: deleteRule,
    isSuccess: isDeleteSuccess,
    isPending: isRuleDeleting,
  } = useDeleteRule();
  const {
    mutateAsync: createRule,
    isSuccess: isCreateSuccess,
    data: createdRuleData,
    isPending,
  } = useCreateRule();

  useEffect(() => {
    if (isUpdateRuleSuccess) {
      refetchRules();
    }
  }, [isUpdateRuleSuccess, refetchRules]);

  useEffect(() => {
    if (isGetSampleUsersSuccess && sampleUsers?.length) {
      setShowSampleUsers(true);
    }
  }, [isGetSampleUsersSuccess, sampleUsers]);

  useEffect(() => {
    if (isCreateSuccess) {
      refetchRules();
      setIsNewRuleAdded(false);
    }
  }, [isCreateSuccess, refetchRules, createdRuleData, setIsNewRuleAdded]);

  useEffect(() => {
    if (isDeleteSuccess) {
      refetchRules();
      resetActiveTab();
    }
  }, [isDeleteSuccess, refetchRules, resetActiveTab]);

  useEffect(() => {
    setUpdatedRule((prevRule) => ({
      ...prevRule,
      ruleDescription: updatedCondition
        ? `If users ${updatedCondition.attribute} ${
            updatedCondition.operator
          } ${updatedCondition.values.join(', ')}`
        : '',
    }));
  }, [updatedCondition]);

  useEffect(() => {
    setUpdatedCondition(rule.conditions[0]);
  }, [rule.conditions]);

  const handleConfirmBtnClick = async () => {
    if (isNewRule) {
      delete updatedRule.ruleKey;
      delete updatedCondition.attributeKey;
      updatedRule.name = `EXTERNAL_${new Date().toISOString()}`;
      updatedRule.conditions = [updatedCondition];
      updatedRule.status = 0;
      await createRule(
        { ...updatedRule },
        {
          onSuccess: (response, variables) => {
            if (response.status === 200 || response.status === 201) {
              setNewRuleName(variables.name);
              setIsRuleConfirmed(true);
              setErrorMessage('');
            } else if (response.errorMessage) {
              setErrorMessage(response.errorMessage);
              setIsRuleConfirmed(false);
              setShowCriticalBanner(true);
            }
          },
        }
      );
    } else {
      updateRule(
        {
          ...rule,
          status: 0,
          ruleDescription: updatedRule.ruleDescription,
          conditions: [updatedCondition],
        },
        {
          onError: (response) => {
            setErrorMessage(response.message);
            setIsRuleConfirmed(false);
            setShowCriticalBanner(true);
          },
          onSuccess: (response) => {
            if (response.errorMessage) {
              setErrorMessage(response.errorMessage);
              setIsRuleConfirmed(false);
              setShowCriticalBanner(true);
            } else {
              setIsRuleConfirmed(true);
              setErrorMessage('');
            }
          },
        }
      );
    }
  };

  const runShowSampleUsers = useCallback(() => {
    getSampleUsers(rule.ruleKey ?? 0, {
      onSuccess: (response) => {
        if (response.status === 200 || response.status === 201 || response.ok) {
          setSampleUsers(response.data?.usersData);
          setErrorMessage('');
        } else if (response.errorMessage || response.error) {
          setErrorMessage(response.errorMessage ?? '');
          setShowCriticalBanner(true);
        }
      },
    });
  }, [rule.ruleKey, getSampleUsers, setShowCriticalBanner, setErrorMessage]);

  useEffect(() => {
    if (isRecommendedRule) {
      runShowSampleUsers();
    }
  }, [isRecommendedRule, runShowSampleUsers]);

  const showSampleUsersBtn =
    !showSampleUsers && !isNewRule && !isGetSampleUsersPending;

  const isConditionValueEmpty = !OPERATORS_WITH_NO_VALIDATION.includes(
    updatedCondition.operator
  )
    ? isEmpty(updatedCondition.values)
    : false;

  const isConfirmBtnDisabled =
    isEmpty(updatedCondition.attribute) ||
    isEmpty(updatedCondition.operator) ||
    isConditionValueEmpty ||
    isPending ||
    isRuleUpdating;

  return (
    <div className='Ext-RuleTabs-content-ruleDetails'>
      <ModalIsland
        kind='warning'
        alertTitle='Confirm to remove rule?'
        alertSubtitle='This action cannot be undone. The selected rule will be removed from the list.'
        onClose={() => setIsRemoveModal(false)}
        isOpen={isRemoveModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Delete' size='medium' color='neutral-100' />}
            isLoading={isRuleDeleting}
            loaderText='Removing'
            isDisabled={isRuleDeleting}
            onClick={() => {
              deleteRule(
                {
                  ...rule,
                  conditions: [updatedCondition],
                },
                {
                  onSuccess: (response) => {
                    if (response.status === 200 || response.status === 201) {
                      setShowRemoveSuccessBanner(true);
                      setIsRemoveModal(false);
                      setErrorMessage('');
                    } else if (response.errorMessage || response.error) {
                      setErrorMessage(response.errorMessage ?? '');
                      setShowCriticalBanner(true);
                    }
                  },
                }
              );
            }}>
            {l('Remove')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={() => {
              setIsRemoveModal(false);
            }}>
            {l('Cancel')}
          </Button>
        }
      />
      <Typography kind='h3' color='--color-foreground-neutral-bold'>
        {showSampleUsersBtn
          ? l('Review Rule and Make Changes if necessary')
          : l('Create New Rule')}
      </Typography>
      <RuleCondition
        // rule={rule}
        ruleCondition={rule.conditions}
        setUpdatedCondition={setUpdatedCondition}
        setIsRuleConfirmed={setIsRuleConfirmed}
      />
      {showSampleUsersBtn ? (
        <div className=''>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            leftIcon={
              <Icon kind='ArrowDown' size='medium' color='neutral-100' />
            }
            onClick={runShowSampleUsers}>
            {l('Show User Samples')}
          </Button>
        </div>
      ) : (
        ''
      )}
      {sampleUsers?.length && showSampleUsers ? (
        <div className=''>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            leftIcon={<Icon kind='ArrowUp' size='medium' color='neutral-100' />}
            onClick={() => {
              setShowSampleUsers(false);
            }}>
            {l('Hide User Samples')}
          </Button>
        </div>
      ) : (
        ''
      )}
      {isGetSampleUsersPending && (
        <div className='Ext-RuleTabs-content-ruleDetails--isLoading'>
          <Loader
            kind='loop'
            format='inline'
            color='brand'
            className='Ext-RuleTabs-content-ruleDetails-loader'
          />
          <div>
            <Typography
              kind='body2-bold'
              color='--color-foreground-primary-medium'>
              {l('Loading sample users')}
            </Typography>
          </div>
        </div>
      )}
      {!isEmpty(sampleUsers)
        ? showSampleUsers && (
            <div className='Ext-RuleTabs-content-ruleDetails-usersContainer'>
              <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-title'>
                <Typography kind='body-2'>
                  {l('Based on the following user data sample:')}
                </Typography>
                {rule.data?.impactedUserCount && (
                  <div className=''>
                    <Typography
                      kind='body2'
                      className='Ext-RuleTabs-content-ruleDetails-usersContainer-applies'>
                      <Typography
                        kind='body2'
                        color='--color-foreground-neutral-subtle'>
                        {l('Applies to')}
                      </Typography>
                      : {rule.data.impactedUserCount} {l('External Users')}
                    </Typography>
                  </div>
                )}
              </div>
              {sampleUsers?.map((user) => (
                <Box className='Ext-RuleTabs-content-ruleDetails-usersContainer-user'>
                  <Avatar size='small' />
                  <div>
                    <Typography kind='body3'>User</Typography>
                    <Typography kind='h3'>{user.Username}</Typography>
                  </div>
                  <div>
                    <Typography kind='body3'>Employee Type</Typography>
                    <Typography kind='body2'>
                      {user['Employee Type'] ?? '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography kind='body3'>End date</Typography>
                    <Typography kind='body2'>{user['End date']}</Typography>
                  </div>
                </Box>
              ))}
            </div>
          )
        : isGetSampleUsersSuccess && (
            <div className='Ext-RuleTabs-content-ruleDetails-noUsers'>
              {l('No matching users.')}
            </div>
          )}
      <div className='Ext-RuleTabs-content-actions'>
        {!isNewRule && (
          <Button
            type='button'
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Delete' size='medium' color='neutral-100' />}
            onClick={() => {
              setIsRemoveModal(true);
            }}>
            {l('Remove')}
          </Button>
        )}
        {rule.status === 0 && isRuleConfirmed ? (
          <Button
            type='button'
            kind='filled'
            size='medium'
            isSuccess
            successText='Confirmed'
            leftIcon={<Icon kind='Check' />}>
            {l('Confirmed')}
          </Button>
        ) : (
          <Button
            type='button'
            kind='filled'
            size='medium'
            isLoading={isPending || isRuleUpdating}
            loaderText='Confirming'
            isDisabled={isConfirmBtnDisabled}
            onClick={async () => {
              await handleConfirmBtnClick();
            }}>
            {l('Confirm')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default TabContent;
